import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Modal } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import RecordBadge from '../common/RecordBadge';
import { Skeleton } from '../loading';
import { deleteDemoItems, previewDemoItems } from '../../services/packages';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';
import { normalizeId } from '../../utils/ids';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';

// How many of the person's linking records the dialog names before it says
// "and n more". The API sends up to 25; a dialog is not a report.
export const NAMED_SHOWN = 5;

const records = n => (n === 1 ? '1 demo record' : `${n} demo records`);

/** "Removed 3 of 5 demo records" for a removal that stopped part-way. */
function partialSentence(partial) {
	const done = Number(partial?.deleted) || 0;
	const left = Number(partial?.left) || 0;
	return `Removed ${done} of ${done + left} demo records, then it stopped. What was removed is in the collection's history; remove the rest to finish.`;
}

/**
 * REMOVE DEMO DATA… (KPR-343, KPR-363): the confirmation for taking out the
 * example records a Discover package brought. It asks the API what the
 * removal would do (GET …/demo-items/preview, which writes nothing) and says
 * it before the person decides: how many records go, and which of THEIR OWN
 * records hold a link to one of them and will lose it. The removal is still
 * delete-and-unlink; the owner's ruling (2026-10-09) is that the warning is
 * enough, so there is no typed word. Sent with the preview's token, a removal
 * the list has moved under is refused and the dialog looks again.
 */
export default function RemoveDemoDataModal({ open, collectionId, onClose, onRemoved }) {
	const { message } = App.useApp();
	const cardDefs = useOptionalCardDefinitions();
	const [preview, setPreview] = useState(null);
	const [loadError, setLoadError] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState(null);

	const look = useCallback(() => setAttempt(n => n + 1), []);

	useEffect(() => {
		if (!open) return undefined;
		let cancelled = false;
		setLoadError(false);
		previewDemoItems(collectionId).then((data) => {
			if (cancelled) return;
			setPreview(data);
		}).catch(() => {
			if (!cancelled) setLoadError(true);
		});
		return () => { cancelled = true; };
	}, [open, collectionId, attempt]);

	// Each opening starts from nothing: never the last look at an earlier list.
	useEffect(() => {
		if (open) return;
		setPreview(null);
		setLoadError(false);
		setNotice(null);
		setBusy(false);
	}, [open]);

	const count = preview?.examples?.count ?? 0;
	const kept = preview?.examples?.kept ?? 0;
	const links = preview?.links || { records: 0, hidden: 0, named: [] };
	const shown = (links.named || []).slice(0, NAMED_SHOWN);
	const more = Math.max(0, links.records - shown.length);
	const nothingLeft = Boolean(preview) && count === 0;

	const submit = async () => {
		if (!preview || busy) return;
		setBusy(true);
		setNotice(null);
		try {
			const out = await deleteDemoItems(collectionId, preview.previewToken);
			message.success(out.deleted === 1 ? 'Removed 1 demo record.' : `Removed ${out.deleted} demo records.`);
			onRemoved?.(out);
			onClose?.();
		} catch (e) {
			const code = apiErrorCode(e);
			const partial = e?.response?.data?.partial;
			if (code === 'demo_preview_stale') {
				setNotice({ type: 'info', text: 'The demo data changed while you were looking. Check the list again, then remove.' });
				setPreview(null);
				look();
			} else if (partial) {
				setNotice({ type: 'warning', text: partialSentence(partial) });
				setPreview(null);
				onRemoved?.(partial);
				look();
			} else {
				setNotice({ type: 'error', text: apiErrorMessage(e, 'The demo data could not be removed.') });
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title={nothingLeft ? 'No demo data left' : 'Remove the demo data?'}
			okText="Remove"
			cancelText={nothingLeft ? 'Close' : 'Cancel'}
			okButtonProps={{ danger: true, loading: busy, disabled: !preview || nothingLeft, style: nothingLeft ? { display: 'none' } : undefined }}
			cancelButtonProps={{ autoFocus: true }}
			onOk={submit}
			onCancel={onClose}
			destroyOnHidden
			className="remove-demo-data"
			width={520}
		>
			<DialogLoadState
				loaded={Boolean(preview)}
				error={loadError}
				label="what removing the demo data would do"
				onRetry={look}
				skeleton={<Skeleton variant="list-row" count={3} />}
				minHeight={96}
			>
				{nothingLeft ? (
					<p>There is no demo data left in this collection.</p>
				) : (
					<>
						<p className="remove-demo-data__lead">
							{count === 1 ? 'This demo record' : `These ${records(count)}`} will be removed. Each one stays in the collection&rsquo;s history for 30 days, so you can restore it.
						</p>
						{kept > 0 ? (
							<p className="remove-demo-data__note">
								{kept === 1 ? '1 demo record stays' : `${kept} demo records stay`}, because its card never deletes records.
							</p>
						) : null}
						{links.records > 0 ? (
							<section className="remove-demo-data__links" aria-label="Links you will lose">
								<p className="remove-demo-data__lead">
									{links.records === 1 ? '1 of your records links' : `${links.records} of your records link`} to demo records. Those links will be lost:
								</p>
								<ul className="remove-demo-data__list">
									{shown.map((row) => {
										const card = cardDefs?.cardsById?.[normalizeId(row.cardId)] || {};
										return (
											<li key={row.id} className="remove-demo-data__row">
												<RecordBadge kind="item" record={card} kicker={card.name} title={row.title || 'Untitled record'} size="xs" />
											</li>
										);
									})}
									{more > 0 ? <li className="remove-demo-data__more">and {more} more</li> : null}
								</ul>
								<p className="remove-demo-data__note">The records themselves stay. If you restore a demo record from history, its links come back.</p>
							</section>
						) : null}
					</>
				)}
			</DialogLoadState>
			{notice ? <Alert className="remove-demo-data__alert" type={notice.type} showIcon message={notice.text} /> : null}
		</Modal>
	);
}
